/**
 * Documents (and record points) under one collection path.
 *
 * Three sources merge per entry: the vector-store view (audiences resolved
 * to emails/group names, plus ingest-declared tags) and the two platform
 * mirrors — documents and records — which are separate tables under
 * separate keys. Mirror-only rows, still processing or failed before any
 * chunk landed, are appended so support staff see stuck ingestions in place.
 *
 * Rows expand to a metadata block: status, identity, path, chunks, task
 * linkage. Never content — the document summary is the one mirror field that
 * carries source text, and reading it is not part of auditing who can.
 */
import { useQuery } from '@tanstack/react-query';
import { useState, type CSSProperties } from 'react';

import { listOrgCollectionDocuments } from '@optum/api-client/endpoints/collections';
import { listOrgDocuments } from '@optum/api-client/endpoints/documents';
import { listOrgRecords } from '@optum/api-client/endpoints/records';
import type {
  AudienceGroup,
  CollectionDocument,
  IngestedDocument,
  MirrorRecord,
} from '@optum/api-client/types';

import { EmptyBlock, ErrorBlock, LoadingBlock } from '../../ui/AsyncState.js';
import { DividedList } from '../../ui/DividedList.js';
import { StatusBadge } from '../../ui/StatusBadge.js';
import { useToast } from '../../ui/useToast.js';
import { absoluteDateTime } from '../../lib/format.js';
import { qk } from '../../lib/query-keys.js';

interface CollectionDocumentsPanelProps {
  organizationId: string;
  path: string;
}

interface MergedBase {
  doc_id: string;
  collections: string;
  audience: CollectionDocument | null;
}

/**
 * Discriminated on `kind`, because the two halves are tracked in different
 * tables with different keys. Collapsing them into one optional-everything
 * shape is what let a record silently join against the document mirror and
 * come back empty.
 */
export type MergedDocument =
  | (MergedBase & { kind: 'document'; mirror: IngestedDocument | null })
  | (MergedBase & { kind: 'records'; mirror: MirrorRecord | null });

export function mergeSources(
  fromStore: CollectionDocument[],
  fromDocuments: IngestedDocument[],
  fromRecords: MirrorRecord[],
  path: string,
): MergedDocument[] {
  const documentById = new Map(fromDocuments.map((d) => [d.doc_id, d]));
  // A record point carries its record_id in `doc_id`, so that is the join
  // key on the store side. The mirror's other half of the key, model_name,
  // has no counterpart in the vector store at all.
  const recordById = new Map(fromRecords.map((r) => [r.record_id, r]));

  const merged: MergedDocument[] = fromStore.map((doc) =>
    doc.kind === 'records'
      ? {
          doc_id: doc.doc_id,
          collections: doc.collections,
          kind: 'records',
          audience: doc,
          mirror: recordById.get(doc.doc_id) ?? null,
        }
      : {
          doc_id: doc.doc_id,
          collections: doc.collections,
          kind: 'document',
          audience: doc,
          mirror: documentById.get(doc.doc_id) ?? null,
        },
  );

  // Mirror-only rows: ingestions that failed or are still running, with no
  // chunk in the store yet. The store view is subtree-scoped; match the
  // mirror the same way.
  const seen = new Set(merged.map((d) => d.doc_id));
  const inSubtree = (collections: string) =>
    collections === path || collections.startsWith(`${path}/`);

  for (const doc of fromDocuments) {
    if (seen.has(doc.doc_id) || !inSubtree(doc.collections)) continue;
    seen.add(doc.doc_id);
    merged.push({
      doc_id: doc.doc_id,
      collections: doc.collections,
      kind: 'document',
      audience: null,
      mirror: doc,
    });
  }
  for (const record of fromRecords) {
    if (seen.has(record.record_id) || !inSubtree(record.collections)) continue;
    seen.add(record.record_id);
    merged.push({
      doc_id: record.record_id,
      collections: record.collections,
      kind: 'records',
      audience: null,
      mirror: record,
    });
  }
  return merged;
}

export function CollectionDocumentsPanel({ organizationId, path }: CollectionDocumentsPanelProps) {
  const audienceQuery = useQuery({
    queryKey: qk.collectionDocuments(organizationId, path),
    queryFn: () => listOrgCollectionDocuments(organizationId, { path }),
  });
  const mirrorQuery = useQuery({
    queryKey: qk.orgDocuments(organizationId),
    queryFn: () => listOrgDocuments(organizationId, { limit: 500 }),
  });
  // Records are mirrored in their own table behind their own endpoint. The
  // panel read only the document mirror for a while, so every record on the
  // page reported itself untracked no matter what its real status was.
  const recordsQuery = useQuery({
    queryKey: qk.orgRecords(organizationId),
    queryFn: () => listOrgRecords(organizationId, { limit: 500 }),
  });
  const [expanded, setExpanded] = useState<string | null>(null);

  if (audienceQuery.isPending || mirrorQuery.isPending || recordsQuery.isPending) {
    return <LoadingBlock />;
  }
  if (audienceQuery.isError) {
    return (
      <ErrorBlock
        message={
          audienceQuery.error instanceof Error ? audienceQuery.error.message : 'Failed to load'
        }
      />
    );
  }

  const merged = mergeSources(
    audienceQuery.data,
    mirrorQuery.data ?? [],
    recordsQuery.data ?? [],
    path,
  );
  if (merged.length === 0) {
    return <EmptyBlock>No documents in this collection.</EmptyBlock>;
  }

  return (
    <DividedList style={{ display: 'flex', flexDirection: 'column' }}>
      {merged.map((doc) => (
        <li key={`${doc.kind}-${doc.doc_id}`} style={{ paddingTop: 11, paddingBottom: 11 }}>
          <DocumentRow
            doc={doc}
            expanded={expanded === doc.doc_id}
            onToggle={() => setExpanded(expanded === doc.doc_id ? null : doc.doc_id)}
          />
        </li>
      ))}
    </DividedList>
  );
}

function DocumentRow({
  doc,
  expanded,
  onToggle,
}: {
  doc: MergedDocument;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
      <button
        type="button"
        onClick={onToggle}
        style={{ display: 'flex', alignItems: 'baseline', gap: 7, textAlign: 'left' }}
      >
        <code style={{ fontSize: 'var(--text-meta-lg)' }}>{doc.doc_id}</code>
        <span
          style={{
            borderRadius: 'var(--radius-chip)',
            background: 'var(--surface-fill)',
            paddingLeft: 7,
            paddingRight: 7,
            paddingTop: 5,
            paddingBottom: 5,
            fontSize: 'var(--text-meta-lg)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: 'var(--text-muted)',
          }}
        >
          {doc.kind}
        </span>
        {doc.mirror && (
          <StatusBadge
            status={mirrorTone(doc.mirror.status)}
            label={mirrorLabel(doc.mirror.status)}
          />
        )}
        <span
          style={{
            marginLeft: 'auto',
            fontSize: 'var(--text-meta-lg)',
            color: 'var(--text-muted)',
          }}
        >
          {doc.collections}
        </span>
      </button>
      {doc.mirror?.failure_reason && (
        <div style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--danger)' }}>
          {doc.mirror.failure_reason}
        </div>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 7 }}>
        {doc.audience ? (
          <>
            {doc.audience.allowed_groups.map((g) => (
              <GroupChip key={g.id} group={g} />
            ))}
            {doc.audience.allowed_groups.length === 0 && (
              <span style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--danger)' }}>
                no audience
              </span>
            )}
          </>
        ) : (
          <span style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}>
            no chunks in the vector store yet
          </span>
        )}
        {/* Read from the store when the document has chunks, else from the
            mirror, so a still-processing row shows the labels it will get. */}
        {(doc.audience?.tags ?? doc.mirror?.tags ?? []).map((t) => (
          <TagChip key={t} tag={t} />
        ))}
      </div>
      {expanded && <DocumentDetail doc={doc} />}
    </div>
  );
}

function mirrorTone(status: IngestedDocument['status']): string {
  if (status === 'ready') return 'active';
  if (status === 'failed') return 'failed';
  // needs_migration is actionable, not broken — warn rather than error.
  return 'pending';
}

function mirrorLabel(status: IngestedDocument['status']): string {
  return status === 'needs_migration' ? 'needs migration' : status;
}

const DETAIL_STYLE: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '110px minmax(0,1fr)',
  columnGap: 11,
  rowGap: 7,
  borderRadius: 'var(--radius-chip)',
  border: '1px solid var(--border-hairline)',
  background: 'var(--surface-sunken)',
  padding: 11,
  fontSize: 'var(--text-meta-lg)',
};

/**
 * What the platform knows *about* the entry — never what it contains.
 *
 * The document summary used to sit here. It is the one field in the mirror
 * that carries the source text rather than a fact about it, and an operator
 * auditing who can read a document has no business reading it in the process.
 */
export function DocumentDetail({ doc }: { doc: MergedDocument }) {
  if (!doc.mirror) {
    return (
      <div
        style={{
          borderRadius: 'var(--radius-chip)',
          border: '1px solid var(--border-hairline)',
          background: 'var(--surface-sunken)',
          padding: 11,
          fontSize: 'var(--text-meta-lg)',
          color: 'var(--text-muted)',
        }}
      >
        No mirror row for this id — its chunks are in the vector store but the platform recorded no
        ingestion for it.
      </div>
    );
  }
  if (doc.kind === 'records') {
    return <RecordDetail record={doc.mirror} />;
  }
  return <DocumentMirrorDetail mirror={doc.mirror} />;
}

function RecordDetail({ record }: { record: MirrorRecord }) {
  return (
    <dl style={DETAIL_STYLE}>
      <StatusRows status={record.status} taskStatus={record.task_status} />
      <dt style={{ color: 'var(--text-muted)' }}>Model</dt>
      <dd>
        <code>{record.model_name}</code>
      </dd>
      <dt style={{ color: 'var(--text-muted)' }}>Record</dt>
      <dd style={{ wordBreak: 'break-all' }}>
        <code>{record.record_id}</code>
      </dd>
      <dt style={{ color: 'var(--text-muted)' }}>Path</dt>
      <dd>
        <code>{record.collections}</code>
      </dd>
      {record.failure_reason && (
        <>
          <dt style={{ color: 'var(--text-muted)' }}>Failure</dt>
          <dd style={{ color: 'var(--danger)' }}>{record.failure_reason}</dd>
        </>
      )}
      <TaskRow taskId={record.last_task_id} />
    </dl>
  );
}

function DocumentMirrorDetail({ mirror }: { mirror: IngestedDocument }) {
  if (mirror.status === 'needs_migration') {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 7,
          borderRadius: 'var(--radius-chip)',
          border: '1px solid var(--border-hairline)',
          background: 'var(--surface-sunken)',
          padding: 11,
          fontSize: 'var(--text-meta-lg)',
        }}
      >
        <p style={{ color: 'var(--text-muted)' }}>
          Ingested before collections existed: its points carry no audience and no collection
          lineage, so retrieval cannot see it. Migrate the
          <code style={{ marginLeft: 5, marginRight: 5 }}>{mirror.collections}</code> tag to repair
          it in place — no re-embedding.
        </p>
        <dl
          style={{
            display: 'grid',
            gridTemplateColumns: '110px minmax(0,1fr)',
            columnGap: 11,
            rowGap: 7,
          }}
        >
          <dt style={{ color: 'var(--text-muted)' }}>Legacy tag</dt>
          <dd>
            <code>{mirror.collections}</code>
          </dd>
          <dt style={{ color: 'var(--text-muted)' }}>Chunks</dt>
          <dd>{mirror.chunks ?? '—'}</dd>
          <dt style={{ color: 'var(--text-muted)' }}>Updated</dt>
          <dd>{absoluteDateTime(mirror.updated_at)}</dd>
        </dl>
      </div>
    );
  }
  return (
    <dl style={DETAIL_STYLE}>
      <StatusRows status={mirror.status} taskStatus={mirror.task_status} />
      <dt style={{ color: 'var(--text-muted)' }}>Path</dt>
      <dd>
        <code>{mirror.collections}</code>
      </dd>
      {mirror.source_url && (
        <>
          <dt style={{ color: 'var(--text-muted)' }}>Source</dt>
          <dd style={{ wordBreak: 'break-all' }}>{mirror.source_url}</dd>
        </>
      )}
      <dt style={{ color: 'var(--text-muted)' }}>Chunks</dt>
      <dd>{mirror.chunks ?? '—'}</dd>
      <dt style={{ color: 'var(--text-muted)' }}>Updated</dt>
      <dd>{absoluteDateTime(mirror.updated_at)}</dd>
      {mirror.failure_reason && (
        <>
          <dt style={{ color: 'var(--text-muted)' }}>Failure</dt>
          <dd style={{ color: 'var(--danger)' }}>{mirror.failure_reason}</dd>
        </>
      )}
      <TaskRow taskId={mirror.last_task_id} />
    </dl>
  );
}

function StatusRows({ status, taskStatus }: { status: string; taskStatus: string | null }) {
  return (
    <>
      <dt style={{ color: 'var(--text-muted)' }}>Status</dt>
      <dd>
        {status}
        {taskStatus && <span style={{ color: 'var(--text-muted)' }}> (task: {taskStatus})</span>}
      </dd>
    </>
  );
}

function TaskRow({ taskId }: { taskId: string | null }) {
  const toast = useToast();
  if (!taskId) return null;
  return (
    <>
      <dt style={{ color: 'var(--text-muted)' }}>Task</dt>
      <dd>
        <button
          type="button"
          title="Click to copy"
          onClick={() => {
            void navigator.clipboard?.writeText(taskId);
            toast.success('Task id copied');
          }}
          style={{ display: 'inline-flex', alignItems: 'baseline', gap: 7 }}
        >
          <code style={{ wordBreak: 'break-all', fontSize: 'var(--text-meta-lg)' }}>{taskId}</code>
        </button>
      </dd>
    </>
  );
}

function GroupChip({ group }: { group: AudienceGroup }) {
  return (
    <span
      title={group.dangling ? `Matches no group in this organization: ${group.id}` : group.id}
      style={{
        borderRadius: 'var(--radius-pill)',
        border: '1px solid var(--border-hairline)',
        background: 'var(--surface-sunken)',
        padding: '5px 7px',
        fontSize: 'var(--text-meta-lg)',
        ...(group.dangling ? { color: 'var(--text-muted)', textDecoration: 'line-through' } : {}),
      }}
    >
      ⚑ {group.name ?? group.id}
    </span>
  );
}

function TagChip({ tag }: { tag: string }) {
  // Deliberately quieter than a group chip: a tag narrows a search, it never
  // grants access, and the two must not read as the same kind of thing.
  return (
    <span
      title="Ingest-declared label. Narrows retrieval; grants no access."
      style={{
        borderRadius: 'var(--radius-pill)',
        background: 'var(--surface-fill)',
        paddingLeft: 7,
        paddingRight: 7,
        paddingTop: 5,
        paddingBottom: 5,
        fontSize: 'var(--text-meta-lg)',
        color: 'var(--text-muted)',
      }}
    >
      # {tag}
    </span>
  );
}
